import React from 'react';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/solid';
import { CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';

export interface Review {
  id: number;
  promptId: number;
  userId: string;
  title: string;
  content: string;
  helpfulCount: number;
  notHelpfulCount: number;
  createdAt: string;
  updatedAt: string;
  reviewerName?: string; // Changed from userDisplayName to match backend
}

export interface ReviewCardProps {
  review: Review;
  onHelpfulClick?: (reviewId: number, isHelpful: boolean) => void;
  currentUserHelpfulVote?: boolean | null;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  onHelpfulClick,
  currentUserHelpfulVote
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleHelpfulClick = (isHelpful: boolean) => {
    if (onHelpfulClick) {
      onHelpfulClick(review.id, isHelpful);
    }
  };

  return (
    <Card className="mb-4"> {/* Removed conditional styling for pending reviews */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">            
            {review.title && (
              <h3 className="text-lg font-semibold mb-2">{review.title}</h3>
            )}
            
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <UserIcon className="h-4 w-4" />
                <span>{review.reviewerName || 'Anonymous User'}</span>
              </div>
              <div className="flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" />
                <span>{formatDate(review.createdAt)}</span>
              </div>
            </div>
          </div>
          
          {/* Removed pending review badge */}
        </div>
      </CardHeader>

      <CardContent>
        <p className="text-gray-700 mb-4 whitespace-pre-wrap">{review.content}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Was this review helpful?</span>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpfulClick(true)}
                className={`flex items-center gap-1 ${
                  currentUserHelpfulVote === true ? 'text-green-600 bg-green-100' : 'text-gray-600'
                }`}
                disabled={!onHelpfulClick}
              >
                <HandThumbUpIcon className="h-4 w-4" />
                <span className="text-sm">{review.helpfulCount}</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpfulClick(false)}
                className={`flex items-center gap-1 ${
                  currentUserHelpfulVote === false ? 'text-red-600 bg-red-100' : 'text-gray-600'
                }`}
                disabled={!onHelpfulClick}
              >
                <HandThumbDownIcon className="h-4 w-4" />
                <span className="text-sm">{review.notHelpfulCount}</span>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReviewCard;